import { Injectable } from '@angular/core';
import type { AbstractControl, ValidationErrors } from '@angular/forms';

/** Validation configuration for Jodit editor */
export interface JoditValidationConfig {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
}

/**
 * JoditEditorValidatorService - Validation logic for Jodit editor content
 *
 * Handles:
 *
 * - Required field validation
 * - Min/max length validation (on text content, not HTML)
 * - HTML stripping for accurate character counting
 *
 * Extracted from component to improve testability and reusability.
 */
@Injectable()
export class JoditEditorValidatorService {
  /**
   * Validate editor content
   *
   * @param control - The form control to validate
   * @param config - Validation configuration
   * @returns Validation errors or null if valid
   */
  validate(control: AbstractControl, config: JoditValidationConfig): ValidationErrors | null {
    const value = control.value as string;

    // Strip HTML tags for validation
    const text = this._stripHtmlTags(value);

    // Required validation
    if (config.required && !text) {
      return { required: true };
    }

    // Min length validation
    if (config.minLength !== undefined && text.length < config.minLength) {
      return {
        minlength: {
          requiredLength: config.minLength,
          actualLength: text.length,
        },
      };
    }

    // Max length validation
    if (config.maxLength !== undefined && text.length > config.maxLength) {
      return {
        maxlength: {
          requiredLength: config.maxLength,
          actualLength: text.length,
        },
      };
    }

    return null;
  }

  /**
   * Strip HTML tags from string
   *
   * @param value - HTML string
   * @returns Plain text content
   */
  private _stripHtmlTags(value: string): string {
    return value ? value.replace(/<[^>]*>/g, '').trim() : '';
  }

  /**
   * Get character count from HTML string
   *
   * @param value - HTML string
   * @returns Character count (text only, no HTML)
   */
  getCharacterCount(value: string): number {
    return this._stripHtmlTags(value).length;
  }
}
